import { FC, useState, useCallback, useEffect } from 'react'
import {
  Button,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Typography,
  IconButton,
  Alert,
  Spinner,
} from '@material-tailwind/react'
import { PiTrash } from 'react-icons/pi'
import { useTranslation } from 'react-i18next'
import truncate from 'lodash/truncate'
import PlayerMini from './PlayerMini'
import { formatDateTimeString } from '../utils/helpers/dates.helpers'
import { supabase } from '../utils/helpers/supabase.helpers'
import { useSessionContext } from '../contexts/SessionContext'
import { AGAMI_INDOOR_BUCKET } from '../utils/constants/db.constants'
import { useGeneralToast } from '../hooks/toast'
import { SIGNED_URL_VALIDITY } from '../utils/constants/supabase.constants'
import { useDarkMode } from '../contexts/DarkModeContext'
import { BucketFile } from '../types/supabase'

interface AgamiIndoorAudioFilesTableProps {
  audioFiles: BucketFile[]
  refetch: () => void
}

const AgamiIndoorAudioFilesTable: FC<AgamiIndoorAudioFilesTableProps> = ({ audioFiles, refetch }) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  const { user } = useSessionContext()
  const toast = useGeneralToast()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [fileToDelete, setFileToDelete] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [signedUrls, setSignedUrls] = useState<Array<string | null>>([])
  const [generatingUrls, setGeneratingUrls] = useState(false)

  const TABLE_HEAD = [
    { title: t('indoorAudio.table.name') },
    { title: t('indoorAudio.table.firstUpload') },
    { title: t('indoorAudio.table.lastUpdate') },
    { title: t('indoorAudio.table.audio') },
    { title: '' }, // delete icon column
  ]

  const deleteFileFromBucket = useCallback(async () => {
    if (!user || !fileToDelete) return
    setIsDeleting(true)
    try {
      const { error } = await supabase.storage
        .from(AGAMI_INDOOR_BUCKET)
        .remove([`${user.id}/audio/${fileToDelete}`])

      if (error) {
        return
      }

      toast.success(t('indoorAudio.messages.fileDeleted'))
      refetch()
      setDeleteDialogOpen(false)
    } catch {
      // Delete file error
    } finally {
      setIsDeleting(false)
    }
  }, [user, fileToDelete, toast, t, refetch])

  const getSignedUrls = useCallback(async () => {
    if (!user || !audioFiles || audioFiles?.length === 0) return
    setGeneratingUrls(true)
    try {
      const paths = audioFiles.map((audioFile) => `${user.id}/audio/${audioFile.name}`)
      const { data, error } = await supabase.storage
        .from(AGAMI_INDOOR_BUCKET)
        .createSignedUrls(paths, SIGNED_URL_VALIDITY)

      if (error) {
        return
      }

      const urls = data?.map((url) => url.signedUrl)
      setSignedUrls(urls)
    } catch {
      // Get signed URLs error
    } finally {
      setGeneratingUrls(false)
    }
  }, [user, audioFiles])

  useEffect(() => {
    getSignedUrls()
  }, [audioFiles])

  useEffect(() => {
    if (!deleteDialogOpen) {
      setFileToDelete(null)
    }
  }, [deleteDialogOpen])

  return (
    <>
      <Dialog open={deleteDialogOpen} handler={() => setDeleteDialogOpen((prev) => !prev)}>
        <DialogHeader>{t('indoorAudio.dialog.deleteTitle', { fileName: fileToDelete })}</DialogHeader>
        <DialogBody>
          <Alert color="orange" variant={darkMode ? 'outlined' : 'ghost'}>
            {t('indoorAudio.dialog.cannotBeUndone')}
          </Alert>
        </DialogBody>
        <DialogFooter className="gap-4">
          <Button
            variant="outlined"
            size="sm"
            onClick={() => setDeleteDialogOpen(false)}
            disabled={isDeleting}
          >
            {isDeleting ? <Spinner className="h-4 w-4" /> : t('common.cancel')}
          </Button>
          <Button size="sm" color="red" disabled={isDeleting} onClick={deleteFileFromBucket}>
            {isDeleting ? <Spinner className="h-4 w-4" /> : t('indoorAudio.dialog.deleteButton')}
          </Button>
        </DialogFooter>
      </Dialog>

      <div className="w-full overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              {TABLE_HEAD.map((head) => (
                <th
                  className="border-b border-blue-gray-100 bg-blue-gray-500/30 dark:bg-primary-600 p-4"
                  key={head.title}
                >
                  <Typography variant="small" className="font-bold leading-none flex gap-2">
                    {head.title}
                  </Typography>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {audioFiles?.map((audioFile, idx) => (
              <tr
                key={audioFile.id}
                className="even:bg-blue-gray-50/50 dark:even:bg-primary-500 hover:bg-white-yellow dark:hover:bg-primary-300 cursor-pointer"
              >
                <td className="px-4">
                  <Typography title={audioFile.name} variant="small" className="font-black">
                    {truncate(audioFile.name, {
                      separator: ' ',
                    })}
                  </Typography>
                </td>
                <td className="px-4">
                  <Typography variant="small">{formatDateTimeString(audioFile.created_at)}</Typography>
                </td>
                <td className="px-4">
                  <Typography variant="small">{formatDateTimeString(audioFile.updated_at)}</Typography>
                </td>
                <td className="px-4">
                  {generatingUrls ? (
                    <Spinner className="h-4 w-4" />
                  ) : (
                    <PlayerMini audioUrl={signedUrls[idx] ?? ''} />
                  )}
                </td>
                <td className="px-4 text-right">
                  <IconButton
                    variant="text"
                    size="sm"
                    color="yellow"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setFileToDelete(audioFile.name)
                      setDeleteDialogOpen(true)
                    }}
                  >
                    <PiTrash className="text-primary dark:text-base-gray-500 text-[20px]" />
                  </IconButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

export default AgamiIndoorAudioFilesTable
